/**
 * SubZoneForm — create/edit form for a sub-zone (route polygon).
 *
 * Rendered by MapboxMain in two places: as a modal (editing an existing
 * sub-zone) and as a side panel while drawing a new polygon. The polygon
 * itself lives in the form's hidden `points` field, filled by the map's
 * draw handler — submitting without a drawn polygon is rejected. Optional
 * start/end addresses (AddressInput1) mark the route's endpoints.
 */
import React, { useState, useEffect } from "react";
import { Form, Input, Spin, Popconfirm, Button, Popover, notification } from "antd";
import { useDispatch } from "react-redux";
import { isEmpty, sample } from "lodash";

import "./SubZone.scss";
import { SubmitIcon, DeleteIcon, EditAddressIcon } from "components/Icons";
import { PrimaryButton, TextButton } from 'components/Buttons';
import AddressInput1 from "../../Inputs/AddressInput1";

import { doUpdateSubZone, doCreateSubZone, doDeleteSubZone } from "modules/subZone/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";
import { COLORS_SUBZONE } from "constants";

const SubZoneForm = ({
  zone,
  form,
  state,
  isFetching,
  handleCancel,
  onClosePopup,
  stopsInNewZones,
  editPoints,
}) => {
  const action = state.action;
  const dispatch = useDispatch();
  const [stateColor, setStateColor] = useState({
    visiblePopover: false,
    color: sample(COLORS_SUBZONE),
  });

  useEffect(() => {
    setStateColor({ ...stateColor, color: form.getFieldValue('color')});
  // eslint-disable-next-line
  }, [state]);

  const handleDelete = async () => {
    await dispatch(doDeleteSubZone(state.subZone._id));
    handleCancel();
    onClosePopup();
    await dispatch(doDetailZone(zone._id));
  };

  const handleSelectedColor = (color) => {
    setStateColor({ ...stateColor, visiblePopover: false, color });
  };

  const handleVisibleChange = (visible) => {
    setStateColor({ ...stateColor, visiblePopover: visible });
  };

  const handleSubmit = async (values) => {
    const points = form.getFieldValue('points');
    if (points) {
      const params = { ...values, points: form.getFieldValue('points'), color: stateColor.color };
      if (values.startAddress) {
        params.startAddress = values.startAddress.address;
        params.startLat = values.startAddress.latLng.lat;
        params.startLng = values.startAddress.latLng.lng;
      }
      if (values.endAddress) {
        params.endAddress = values.endAddress.address;
        params.endLat = values.endAddress.latLng.lat;
        params.endLng = values.endAddress.latLng.lng;
      }
      console.log(params);
      if (state.action === 'new') {
        await dispatch(doCreateSubZone({ ...params, zone: zone._id }));
      } else {
        await dispatch(doUpdateSubZone({ id: state.subZone._id, ...params }));
      }
      handleCancel();
      onClosePopup();
      await dispatch(doDetailZone(zone._id));
    } else {
      notification.error({
        message: "Please draw polyline",
        placement: "bottomRight",
      });
    }
  };

  return (
    <Spin spinning={isFetching}>
      <div className="SubZoneForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleSubmit}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="FormHeader">
            <div className="left">
              {action === 'new' ? `New Sub Zone(${stopsInNewZones || 0} stops)` : "Edit Sub Zone"}
            </div>
          </div>
          <div className="FormContent">
            <div className="SubZoneName">
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Required" }]}
                style={{ width: "100%" }}
              >
                <Input
                  size="large"
                  placeholder="Name"
                />
              </Form.Item>
              <Popover
                content={
                  <div className="optionsColors">
                    {COLORS_SUBZONE.map((color, index) => {
                      return (
                        <Button
                          className={`SubZoneColor ${
                            color === stateColor.color ? "selected" : ""
                          }`}
                          key={index}
                          onClick={() => handleSelectedColor(color)}
                        >
                          <span
                            className="color"
                            style={{ backgroundColor: `#${color}` }}
                          />
                        </Button>
                      );
                    })}
                  </div>
                }
                trigger="click"
                visible={stateColor.visiblePopover}
                onVisibleChange={handleVisibleChange}
              >
                <Button size="large" className="SubZoneColor">
                  <span
                    className="color"
                    style={{ backgroundColor: `#${stateColor.color}` }}
                  />
                </Button>
              </Popover>
            </div>
            <Form.Item
              label="Start Address"
              name="startAddress"
              rules={[
                {
                  validator: (_, value) => {
                    if (
                      value?.address &&
                      value?.latLng?.lat &&
                      value?.latLng?.lng
                    ) {
                      return Promise.resolve();
                    }

                    if (isEmpty(value?.address)) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error("Invalid"));
                  },
                },
              ]}
            >
              <AddressInput1
                size="large"
                placeholder="Start Address"
                onSelect={(value) =>
                  form.setFieldsValue({ startAddress: value })
                }
              />
            </Form.Item>
            <Form.Item
              label="End Address"
              name="endAddress"
              rules={[
                {
                  validator: (_, value) => {
                    if (
                      value?.address &&
                      value?.latLng?.lat &&
                      value?.latLng?.lng
                    ) {
                      return Promise.resolve();
                    }

                    if (isEmpty(value?.address)) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error("Invalid"));
                  },
                },
              ]}
            >
              <AddressInput1
                size="large"
                placeholder="End Address"
                onSelect={(value) =>
                  form.setFieldsValue({ endAddress: value })
                }
              />
            </Form.Item>
            {editPoints && (
              <div className="Center">
                <PrimaryButton
                  type="primary"
                  label="Redigera område"
                  style={{
                    margin: "0 auto 24px"
                  }}
                  icon={<EditAddressIcon />}
                  onClick={editPoints}
                />
              </div>
            )}
          </div>
          <div className="FormFooter">
            <div className="Left">
              {action !== 'new' && (
                <Popconfirm
                  title="Radera delzon"
                  description="Är du säker på att du vill radera denna delzon?"
                  onConfirm={handleDelete}
                  okText="Ja"
                  cancelText="Nej"
                >
                  <Button className="delete" danger><DeleteIcon /></Button>
                </Popconfirm>
              )}
            </div>
            <div className="Right">
              <TextButton
                key="cancel"
                label="Avbryt"
                onClick={handleCancel}
              />
              <PrimaryButton
                htmlType="submit"
                type="primary"
                label="Bekräfta"
                icon={<SubmitIcon />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default SubZoneForm;
